/**
 * Tuner.js
 * 
 * This component provides a musical instrument tuner that uses the device's microphone
 * to detect pitch and help musicians tune their instruments. Key features include:
 * 
 * - Real-time pitch detection using the Web Audio API
 * - Visual feedback showing how close the detected note is to perfect pitch
 * - Support for standard guitar tuning (E A D G B E)
 * - Manual note playback for reference tones
 * - Mobile-friendly interface with clear visual indicators
 * 
 * The tuner displays the detected note, frequency in Hz, and cents deviation from
 * the perfect pitch, helping musicians achieve accurate tuning for their instruments.
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Alert,
  Paper,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

// Notes for the buttons
const NOTES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
const A4_FREQ = 440;
const NOTE_HOLD_TIME = 1000; // Time to hold the note display in ms
const METER_DECAY_RATE = 0.95; // Rate at which the meter decays

const Tuner = () => {
  const { t } = useTranslation();
  const [note, setNote] = useState('');
  const [frequency, setFrequency] = useState(0);
  const [cents, setCents] = useState(0);
  const [isListening, setIsListening] = useState(false);
  const [error, setError] = useState('');
  const audioContext = useRef(null);
  const analyser = useRef(null);
  const mediaStreamSource = useRef(null);
  const animationFrame = useRef(null);
  const lastNoteTime = useRef(null);
  const lastNote = useRef(null);
  const decayingCents = useRef(0);
  const smoothedFrequency = useRef(0);

  // Function to update display with smoothing
  const updateDisplay = (newNote, newFreq, newCents) => {
    const now = Date.now();
    
    // Update frequency with smoothing
    if (newFreq > 0) {
      smoothedFrequency.current = smoothedFrequency.current * 0.8 + newFreq * 0.2;
    }

    // Update cents with decay
    if (newCents !== null) {
      decayingCents.current = newCents;
    } else {
      decayingCents.current *= METER_DECAY_RATE;
    }

    // Note holding logic
    if (newNote) {
      if (newNote !== lastNote.current) {
        lastNote.current = newNote;
        lastNoteTime.current = now;
      } else if (now - lastNoteTime.current < NOTE_HOLD_TIME) {
        // Extend the hold time
        lastNoteTime.current = now;
      }
    }

    // Clear note if hold time has expired
    if (lastNoteTime.current && now - lastNoteTime.current > NOTE_HOLD_TIME) {
      lastNote.current = null;
      lastNoteTime.current = null;
    }

    // Update state
    setNote(lastNote.current || '');
    setFrequency(smoothedFrequency.current);
    setCents(decayingCents.current);
  };

  // Function to get note from frequency
  const getNote = (frequency) => {
    if (!frequency || frequency <= 0 || !isFinite(frequency)) {
      return { noteName: undefined, cents: 0 };
    }
    
    // Get note number from frequency, -69 gives A4 = 0
    const noteNum = 12 * Math.log2(frequency / A4_FREQ);
    
    // Calculate note index and cents
    const noteInt = Math.round(noteNum);
    const cents = Math.round((noteNum - noteInt) * 100);
    
    // Calculate octave-independent note index
    const noteIndex = ((noteInt + 9) % 12 + 12) % 12;
    const noteName = NOTES[noteIndex];

    return { noteName, cents };
  };

  // Function to start listening
  const startListening = async () => {
    try {
      if (!audioContext.current) {
        audioContext.current = new (window.AudioContext || window.webkitAudioContext)();
        if (audioContext.current.state === 'suspended') {
          await audioContext.current.resume();
        }
      }

      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false
        }
      });
      
      analyser.current = audioContext.current.createAnalyser();
      analyser.current.minDecibels = -90;
      analyser.current.maxDecibels = -10;
      analyser.current.smoothingTimeConstant = 0.85;
      analyser.current.fftSize = 2048;
      
      mediaStreamSource.current = audioContext.current.createMediaStreamSource(stream);
      mediaStreamSource.current.connect(analyser.current);

      const bufferLength = analyser.current.frequencyBinCount;
      const dataArray = new Float32Array(bufferLength);

      const updatePitch = () => {
        analyser.current.getFloatTimeDomainData(dataArray);
        
        // Calculate RMS
        const rms = Math.sqrt(dataArray.reduce((acc, val) => acc + val * val, 0) / bufferLength);
        
        if (rms > 0.02) {
          const frequency = autoCorrelate(dataArray, audioContext.current.sampleRate);
          
          if (frequency > 0) {  // Valid frequency detected
            const roundedFreq = Math.round(frequency);
            const { noteName, cents } = getNote(frequency);
            
            if (noteName) {
              updateDisplay(noteName, roundedFreq, cents);
            } else {
              updateDisplay(null, roundedFreq, null);
            }
          } else {
            updateDisplay(null, 0, null);
          }
        } else {
          updateDisplay(null, 0, null);
        }

        animationFrame.current = requestAnimationFrame(updatePitch);
      };

      setIsListening(true);
      setError('');
      updatePitch();
    } catch (err) {
      console.error('Tuner error:', err);
      setError(`${t('tuner.microphoneAccess')}: ${err.message}`);
    }
  };

  // Function to stop listening
  const stopListening = () => {
    if (animationFrame.current) {
      cancelAnimationFrame(animationFrame.current);
    }
    if (mediaStreamSource.current) {
      mediaStreamSource.current.disconnect();
    }
    if (audioContext.current) {
      audioContext.current.close();
      audioContext.current = null;
    }
    setIsListening(false);
    setNote('');
    setFrequency(0);
    setCents(0);
    lastNote.current = null;
    lastNoteTime.current = null;
    decayingCents.current = 0;
    smoothedFrequency.current = 0;
  };

  // Function to play a note
  const playNote = (noteName) => {
    try {
      if (!audioContext.current) {
        audioContext.current = new (window.AudioContext || window.webkitAudioContext)();
      }

      const baseFreq = A4_FREQ * Math.pow(2, (NOTES.indexOf(noteName) - NOTES.indexOf('A')) / 12);
      const osc = audioContext.current.createOscillator();
      const gain = audioContext.current.createGain();

      osc.frequency.value = baseFreq;
      osc.type = 'sine';
      
      gain.gain.setValueAtTime(0, audioContext.current.currentTime);
      gain.gain.linearRampToValueAtTime(0.5, audioContext.current.currentTime + 0.1);
      gain.gain.linearRampToValueAtTime(0, audioContext.current.currentTime + 1);

      osc.connect(gain);
      gain.connect(audioContext.current.destination);

      osc.start();
      osc.stop(audioContext.current.currentTime + 1);
    } catch (err) {
      console.error('Error playing note:', err);
      setError(`Error playing note: ${err.message}`);
    }
  };

  // Cleanup
  useEffect(() => {
    return () => {
      if (animationFrame.current) {
        cancelAnimationFrame(animationFrame.current);
      }
      if (audioContext.current) {
        audioContext.current.close();
      }
    };
  }, []);

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
        p: 3,
      }}
    >
      {error && <Alert severity="error" sx={{ width: '100%' }}>{error}</Alert>}

      {/* Main Tuner Display */}
      <Paper
        elevation={3}
        sx={{
          p: 4,
          borderRadius: 4,
          width: '100%',
          maxWidth: 600,
          textAlign: 'center',
          position: 'relative',
          bgcolor: (theme) => theme.palette.mode === 'dark' ? 'primary.dark' : 'white',
        }}
      >
        <Button
          variant="contained"
          onClick={isListening ? stopListening : startListening}
          sx={{ py: 2, px: 4 }}
        >
          {isListening ? t('tuner.stopListening') : t('tuner.startListening')}
        </Button>

        {isListening && (
          <>
            <Typography variant="h1" sx={{ mb: 2, mt: 4 }}>
              {note || '-'}
            </Typography>
            <Box sx={{ position: 'relative', height: 20, bgcolor: 'grey.300', borderRadius: 2, mb: 2 }}>
              {/* In-tune range indicator */}
              <Box
                sx={{
                  position: 'absolute',
                  height: '100%',
                  left: 'calc(50% - 10px)', // Center the 20px wide zone
                  width: 20,
                  bgcolor: 'rgba(46, 125, 50, 0.2)', // Light green background
                  borderRadius: 1
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  height: '100%',
                  left: '50%',
                  width: 10, // Increased from 4 to 10 pixels
                  bgcolor: Math.abs(cents) < 10 ? 'success.main' : 'primary.main', // Increased threshold from 5 to 10
                  transform: `translateX(${cents}px)`,
                  transition: 'transform 0.2s',
                }}
              />
              {/* Center marker to help with tuning */}
              <Box
                sx={{
                  position: 'absolute',
                  height: '100%',
                  left: '50%',
                  width: 2,
                  bgcolor: 'rgba(0, 0, 0, 0.2)',
                  transform: 'translateX(-1px)', // Center the 2px line
                }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary">
              {frequency.toFixed(1)} Hz
            </Typography>
          </>
        )}
      </Paper>

      {/* Note Buttons */}
      <Grid container spacing={1} sx={{ maxWidth: 600 }}>
        {NOTES.map((noteName) => (
          <Grid item xs={3} sm={2} key={noteName}>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => playNote(noteName)}
              sx={{
                height: 48,
                minWidth: 'unset',
              }}
            >
              {noteName}
            </Button>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

// AutoCorrelation function for pitch detection
function autoCorrelate(buffer, sampleRate) {
  const SIZE = buffer.length;
  const MAX_SAMPLES = Math.floor(SIZE/2);
  const MIN_SAMPLES = 0 | (sampleRate / 5000);
  let bestOffset = -1;
  let bestCorrelation = 0;
  let rms = 0;

  // Calculate RMS (signal strength)
  for (let i = 0; i < SIZE; i++) {
    const val = buffer[i];
    rms += val * val;
  }
  rms = Math.sqrt(rms/SIZE);

  if (rms < 0.02) return -1;

  let correlations = new Array(MAX_SAMPLES);
  
  // Find the best correlation
  for (let offset = MIN_SAMPLES; offset < MAX_SAMPLES; offset++) {
    let correlation = 0;
    let count = 0;

    for (let i = 0; i < MAX_SAMPLES; i++) {
      if (i + offset >= buffer.length) break;
      correlation += Math.abs(buffer[i] - buffer[i + offset]);
      count++;
    }

    correlation = 1 - (correlation / count);
    correlations[offset] = correlation;

    if (correlation > bestCorrelation) {
      bestCorrelation = correlation;
      bestOffset = offset;
    }
  }

  if (bestOffset > 0 && bestCorrelation > 0.4) {
    // Refine frequency using interpolation
    let shift = 0;
    if (bestOffset > 1 && bestOffset < MAX_SAMPLES - 1) {
      const before = correlations[bestOffset - 1];
      const after = correlations[bestOffset + 1];
      const delta = after - before;
      shift = delta * 0.5;
    }

    const frequency = sampleRate / (bestOffset + shift);

    if (frequency >= 20 && frequency <= 4000) {
      return frequency;
    }
  }
  
  return -1;
}

export default Tuner;
